/**
 * StorefrontChatWidget — Floating chat bubble for storefront visitors.
 *
 * Features:
 *  • Live chat (Supabase realtime) or WhatsApp handoff
 *  • File attachments (10 MB max)
 *  • Draggable: visitors can drag the floating button to any position when
 *    the admin enables `chatButtonDraggable`. The position is persisted per
 *    device in localStorage so it survives reloads.
 *  • Open-on-demand via `forceOpen` prop (used by the Lite Settings tab to
 *    expose the chat from a stable, non-obstructing place).
 */
import { useState, useEffect, useRef, useCallback, useMemo, forwardRef } from "react";
import { createClient } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { MessageCircle, Send, X, Loader2, Paperclip, Move } from "lucide-react";
import { useAdminSettings } from "@/stores/adminStore";
import { useStorefrontSettings } from "@/stores/storefrontSettingsStore";
import ChatAttachment from "@/components/ChatAttachment";
import { encodeAttachmentPath } from "@/lib/chatAttachments";
import { useRoutedWhatsAppOpener } from "@/lib/whatsapp";
import { useTenantStore } from "@/lib/tenantContext";

const VISITOR_TOKEN_KEY = "khays_visitor_token";
const POSITION_KEY = "khays_chat_button_position";

function getOrCreateVisitorToken(): string {
  let token = localStorage.getItem(VISITOR_TOKEN_KEY);
  if (!token) {
    token = crypto.randomUUID();
    localStorage.setItem(VISITOR_TOKEN_KEY, token);
  }
  return token;
}

function createAnonChatClient(visitorToken: string) {
  return createClient(
    import.meta.env.VITE_SUPABASE_URL,
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    { global: { headers: { "x-visitor-token": visitorToken } }, auth: { persistSession: false } }
  );
}

interface Message {
  id: string;
  conversation_id: string;
  sender_id: string | null;
  content: string | null;
  file_url: string | null;
  file_name: string | null;
  file_type: string | null;
  visitor_name: string | null;
  created_at: string;
}

interface SavedPosition { x: number; y: number; }

function loadPosition(): SavedPosition | null {
  try { const raw = localStorage.getItem(POSITION_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function savePosition(pos: SavedPosition) {
  try { localStorage.setItem(POSITION_KEY, JSON.stringify(pos)); } catch {}
}

function timeAgo(d: string) {
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

const WhatsAppIcon = forwardRef<SVGSVGElement, { className?: string }>(({ className }, ref) => (
  <svg ref={ref} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
));
WhatsAppIcon.displayName = "WhatsAppIcon";

interface Props {
  /** Open programmatically (e.g. from Lite Settings tab). Resets on unmount. */
  forceOpen?: boolean;
  /** Hide the floating launcher button (used when external entry triggers it). */
  hideLauncher?: boolean;
  /** Callback when sheet closes. */
  onCloseRequested?: () => void;
}

export default function StorefrontChatWidget({ forceOpen, hideLauncher, onCloseRequested }: Props = {}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [nameSubmitted, setNameSubmitted] = useState(false);
  const [convoId, setConvoId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [msgText, setMsgText] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [chatMode, setChatMode] = useState<"choose" | "live" | "whatsapp">("choose");
  const [position, setPosition] = useState<SavedPosition | null>(loadPosition());
  const [dragging, setDragging] = useState(false);
  const dragStateRef = useRef<{ pointerId: number; startX: number; startY: number; offX: number; offY: number; moved: boolean } | null>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const adminSettings = useAdminSettings();
  const sfSettings = useStorefrontSettings();
  const visitorToken = useMemo(() => getOrCreateVisitorToken(), []);
  const anonChat = useMemo(() => createAnonChatClient(visitorToken), [visitorToken]);
  const openWa = useRoutedWhatsAppOpener("live_chat", adminSettings.whatsappNumber || "233598429052");
  const tenantId = useTenantStore(s => s.tenantId);
  const btnColor = sfSettings.chatButtonColor || undefined;
  const btnIconColor = sfSettings.chatButtonIconColor || undefined;
  const btnPosition = sfSettings.chatButtonPosition || "bottom-right";
  const posClass = btnPosition === "bottom-left" ? "left-4 md:left-6" : "right-4 md:right-6";
  const widgetEnabled = sfSettings.chatWidgetEnabled !== false;
  const draggable = sfSettings.chatButtonDraggable !== false;

  // Honour external open requests (e.g. Lite Settings entry).
  useEffect(() => { if (forceOpen) setOpen(true); }, [forceOpen]);

  // Restore session from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("khays_chat_session");
    if (saved) {
      try {
        const { convoId: cid, name: n, mode } = JSON.parse(saved);
        if (cid && n) { setConvoId(cid); setName(n); setNameSubmitted(true); setChatMode(mode || "live"); }
      } catch {}
    }
  }, []);

  const loadMessages = useCallback(async () => {
    if (!convoId) return;
    const { data } = await anonChat.from("chat_messages").select("*").eq("conversation_id", convoId).order("created_at", { ascending: true });
    if (data) setMessages(data as Message[]);
  }, [convoId, anonChat]);

  useEffect(() => {
    if (convoId && open && chatMode === "live" && widgetEnabled) loadMessages();
  }, [convoId, open, loadMessages, chatMode, widgetEnabled]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!convoId || chatMode !== "live" || !widgetEnabled) return;
    const channel = supabase.channel(`support-${convoId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "chat_messages", filter: `conversation_id=eq.${convoId}` }, (payload) => {
        const msg = payload.new as Message;
        setMessages(prev => prev.some(m => m.id === msg.id) ? prev : [...prev, msg]);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [convoId, chatMode, widgetEnabled]);

  // Clamp custom position to current viewport on resize so the button never
  // ends up off-screen after rotating a phone or resizing a window.
  useEffect(() => {
    const onResize = () => {
      setPosition(p => {
        if (!p) return p;
        const max = { x: window.innerWidth - 56, y: window.innerHeight - 56 };
        const clamped = { x: Math.max(8, Math.min(p.x, max.x)), y: Math.max(8, Math.min(p.y, max.y)) };
        if (clamped.x !== p.x || clamped.y !== p.y) savePosition(clamped);
        return clamped;
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  if (!widgetEnabled) return null;

  const onPointerDown = (e: React.PointerEvent) => {
    if (!draggable) return;
    const el = launcherRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragStateRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      offX: e.clientX - rect.left,
      offY: e.clientY - rect.top,
      moved: false,
    };
    el.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const st = dragStateRef.current;
    if (!st || st.pointerId !== e.pointerId) return;
    const dx = e.clientX - st.startX;
    const dy = e.clientY - st.startY;
    if (!st.moved && Math.hypot(dx, dy) < 5) return;
    st.moved = true;
    setDragging(true);
    const max = { x: window.innerWidth - 48, y: window.innerHeight - 48 };
    const x = Math.max(8, Math.min(e.clientX - st.offX, max.x));
    const y = Math.max(8, Math.min(e.clientY - st.offY, max.y));
    setPosition({ x, y });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const st = dragStateRef.current;
    dragStateRef.current = null;
    if (!st || st.pointerId !== e.pointerId) return;
    if (st.moved) {
      setDragging(false);
      if (position) savePosition(position);
    } else {
      // Treat as click — the onClick handler will fire too, but guard the
      // handler against drag noise.
    }
  };

  const startChat = async (mode: "live" | "whatsapp") => {
    if (!name.trim()) return;
    setChatMode(mode);
    if (mode === "whatsapp") {
      setNameSubmitted(true);
      localStorage.setItem("khays_chat_session", JSON.stringify({ name: name.trim(), mode: "whatsapp" }));
      return;
    }
    setLoading(true);
    // PHASE C — Route every customer-initiated chat through the platform
    // edge function so it is logged in customer_chat_routes / comm_routing_audit
    // and exposed only to platform staff (not the originating store manager).
    try {
      const { data, error } = await supabase.functions.invoke("route-customer-chat", {
        body: {
          visitorName: name.trim(),
          visitorToken,
          tenantId: tenantId || null,
          sourcePage: typeof window !== "undefined" ? window.location.pathname : null,
        },
      });
      const convId = (data as any)?.conversationId as string | undefined;
      if (!error && convId) {
        setConvoId(convId);
        setNameSubmitted(true);
        localStorage.setItem("khays_chat_session", JSON.stringify({ convoId: convId, name: name.trim(), mode: "live" }));
        setLoading(false);
        return;
      }
      console.warn("[chat] route-customer-chat failed, falling back", error);
    } catch (e) {
      console.warn("[chat] route-customer-chat threw, falling back", e);
    }
    // Fallback: legacy anon-insert path (kept so chat still works if the
    // edge function is temporarily unavailable).
    const { data: convo } = await anonChat.from("chat_conversations").insert({
      type: "support",
      visitor_name: name.trim(),
      visitor_token: visitorToken,
    }).select().single();
    if (convo) {
      setConvoId(convo.id);
      setNameSubmitted(true);
      localStorage.setItem("khays_chat_session", JSON.stringify({ convoId: convo.id, name: name.trim(), mode: "live" }));
    }
    setLoading(false);
  };

  const sendMessage = async () => {
    if (!msgText.trim() || !convoId) return;
    setSending(true);
    await anonChat.from("chat_messages").insert({
      conversation_id: convoId, content: msgText.trim(), visitor_name: name,
    });
    setMsgText("");
    setSending(false);
  };

  const uploadAndSendFile = async (file: globalThis.File) => {
    if (!convoId || file.size > 10 * 1024 * 1024) return;
    setUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "bin";
      const path = `support/${convoId}/${Date.now()}_${Math.random().toString(36).slice(2, 6)}.${ext}`;
      const { error: upErr } = await anonChat.storage.from("chat-attachments").upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      await anonChat.from("chat_messages").insert({
        conversation_id: convoId, visitor_name: name,
        file_url: encodeAttachmentPath(path), file_name: file.name, file_type: file.type,
        content: msgText.trim() || null,
      });
      setMsgText("");
    } catch (err: any) {
      console.error("File upload failed:", err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const sendViaWhatsApp = () => {
    if (!msgText.trim()) return;
    const message = `Hi, I'm ${name.trim()}.\n\n${msgText.trim()}`;
    openWa(message, { customerRef: visitorToken, metadata: { name: name.trim() } });
    setMsgText("");
  };

  const closeSheet = () => { setOpen(false); onCloseRequested?.(); };

  // Custom-positioned launcher (fixed inline) when user has dragged.
  const launcherStyle: React.CSSProperties = position
    ? { position: "fixed", left: position.x, top: position.y, backgroundColor: btnColor || "hsl(var(--primary))", color: btnIconColor || "hsl(var(--primary-foreground))", touchAction: "none" }
    : { backgroundColor: btnColor || "hsl(var(--primary))", color: btnIconColor || "hsl(var(--primary-foreground))", touchAction: draggable ? "none" : undefined };

  if (!open) {
    if (hideLauncher) return null;
    return (
      <button
        ref={launcherRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onClick={(e) => {
          // Suppress click after a drag.
          if (dragging) { setDragging(false); e.preventDefault(); return; }
          setOpen(true);
        }}
        aria-label="Open chat support"
        title={draggable ? "Drag to reposition · click to open" : "Open chat support"}
        className={`${position ? "" : `fixed bottom-20 md:bottom-6 ${posClass}`} z-50 w-12 h-12 rounded-full shadow-lg flex items-center justify-center hover:opacity-90 transition-opacity animate-in fade-in select-none`}
        style={launcherStyle}
      >
        {dragging ? <Move className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
      </button>
    );
  }

  // Sheet position: anchor near the launcher when custom-positioned.
  const sheetStyle: React.CSSProperties = position
    ? {
        position: "fixed",
        left: Math.max(8, Math.min(position.x, window.innerWidth - 340)),
        top: position.y > window.innerHeight / 2
          ? Math.max(8, position.y - 460)
          : Math.min(position.y + 56, window.innerHeight - 460),
      }
    : {};

  return (
    <div
      className={`${position ? "" : `fixed bottom-20 md:bottom-6 ${posClass}`} z-50 w-[min(320px,calc(100vw-2rem))] max-h-[min(450px,calc(100dvh-7rem))] bg-card border border-border rounded-2xl shadow-xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4`}
      style={sheetStyle}
    >
      <div className="flex items-center justify-between px-4 py-3 bg-primary text-primary-foreground">
        <div>
          <p className="text-sm font-bold">Chat with us</p>
          <p className="text-[10px] opacity-80">We typically reply in minutes</p>
        </div>
        <button onClick={closeSheet} className="p-1 hover:bg-white/10 rounded-md"><X className="w-4 h-4" /></button>
      </div>

      {!nameSubmitted ? (
        <div className="p-4 space-y-3">
          <p className="text-xs text-muted-foreground">Enter your name to start chatting</p>
          <input type="text" value={name} onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && name.trim() && startChat("live")}
            placeholder="Your name" maxLength={50}
            className="w-full bg-muted text-foreground rounded-lg px-3 py-2 text-sm border border-border" />
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => startChat("live")} disabled={!name.trim() || loading}
              className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold bg-primary text-primary-foreground disabled:opacity-50">
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageCircle className="w-3.5 h-3.5" />}
              Live Chat
            </button>
            <button onClick={() => startChat("whatsapp")} disabled={!name.trim()}
              className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold bg-[#25D366] text-white disabled:opacity-50 hover:opacity-90 transition-all">
              <WhatsAppIcon className="w-3.5 h-3.5" />
              WhatsApp
            </button>
          </div>
        </div>
      ) : chatMode === "whatsapp" ? (
        <div className="flex-1 flex flex-col">
          <div className="flex-1 p-4 flex flex-col items-center justify-center text-center space-y-2">
            <div className="w-12 h-12 rounded-full bg-[#25D366]/10 flex items-center justify-center">
              <WhatsAppIcon className="w-6 h-6 text-[#25D366]" />
            </div>
            <p className="text-xs font-semibold text-foreground">Chat via WhatsApp</p>
            <p className="text-[10px] text-muted-foreground">Type your message below and it will be sent via WhatsApp</p>
            <button onClick={() => { setChatMode("choose"); setNameSubmitted(false); localStorage.removeItem("khays_chat_session"); }}
              className="text-[10px] text-primary underline">Switch to Live Chat</button>
          </div>
          <div className="p-3 border-t border-border">
            <div className="flex items-center gap-1.5">
              <input type="text" value={msgText} onChange={e => setMsgText(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); sendViaWhatsApp(); } }}
                placeholder="Type a message..." maxLength={2000}
                className="flex-1 bg-muted text-foreground rounded-lg px-3 py-2 text-xs border border-border" />
              <button onClick={sendViaWhatsApp} disabled={!msgText.trim()}
                className="p-2 bg-[#25D366] text-white rounded-lg disabled:opacity-50">
                <WhatsAppIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[200px] max-h-[300px]">
            {messages.length === 0 && (
              <div className="text-center py-6 text-muted-foreground">
                <p className="text-xs">Send a message to get started</p>
                <button onClick={() => { setChatMode("choose"); setNameSubmitted(false); localStorage.removeItem("khays_chat_session"); }}
                  className="text-[10px] text-primary underline mt-2">Switch to WhatsApp</button>
              </div>
            )}
            {messages.map(msg => {
              const isVisitor = !msg.sender_id;
              return (
                <div key={msg.id} className={`flex ${isVisitor ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-xs ${isVisitor ? "bg-primary text-primary-foreground rounded-br-md" : "bg-muted text-foreground rounded-bl-md"}`}>
                    {!isVisitor && <p className="text-[9px] font-semibold mb-0.5 opacity-70">Support</p>}
                    {msg.content && <p className="whitespace-pre-wrap break-words">{msg.content}</p>}
                    {msg.file_url && (
                      <ChatAttachment fileUrl={msg.file_url} fileName={msg.file_name} fileType={msg.file_type} client={anonChat as any} variant={isVisitor ? "outgoing" : "incoming"} />
                    )}
                    <p className={`text-[9px] mt-0.5 ${isVisitor ? "text-primary-foreground/60" : "text-muted-foreground"}`}>{timeAgo(msg.created_at)}</p>
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-3 border-t border-border">
            <input ref={fileInputRef} type="file" accept="image/*,video/*,.pdf,.doc,.docx,.txt"
              onChange={e => { const f = e.target.files?.[0]; if (f) uploadAndSendFile(f); }}
              className="hidden" />
            <div className="flex items-center gap-1.5">
              <button onClick={() => fileInputRef.current?.click()} disabled={uploading || sending}
                className="p-2 text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
                title="Attach file">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
              </button>
              <input type="text" value={msgText} onChange={e => setMsgText(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); sendMessage(); } }}
                placeholder="Type a message..." maxLength={2000}
                className="flex-1 bg-muted text-foreground rounded-lg px-3 py-2 text-xs border border-border" />
              <button onClick={sendMessage} disabled={!msgText.trim() || sending}
                className="p-2 bg-primary text-primary-foreground rounded-lg disabled:opacity-50">
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
